/* eslint-disable @typescript-eslint/no-unused-vars */

import { DocumentPartMetadata } from "../../api/models/document-part-metadata";
import { DimensionDetail } from "../../api/models/dimension-detail";
import {
  CoObjectAdvanced,
  CoObjectItem,
  DBApplication,
  TgkAmountObjectSource,
  TgkDimensionAttributeSource,
  TgkTableObjectSource,
  TgkTextSource,
} from "../../components/interfaces/CoObject";
import { DimensionParameter } from "../../api/models/dimension-parameter";
import { DimensionHierarchy } from "../../api/models/dimension-hierarchy";
import { isLegacyComponentInstance } from "./ComponentInstanceHelper";
import { validateDimensionsAndParams } from "./ObjectCheckerHelper";
import { CoObjectTypeEnum } from "../../api/models";
import { SourceTypeEnum } from "../../api/models/source-type-enum";
import { TgkParameterMetadata } from "../../api/models/tgk-parameter-metadata";
import NotificationManager from "../../common/notification/NotificationManager";
import { formatMessage } from "./LanguageHelper";

export const applyInheritedInfo = async (coObject: CoObjectItem, documentPartMetadata: DocumentPartMetadata) => {
  console.log("Applying inheritance to coObject");
  const source = coObject?.source as any;
  const dbId = source?.dbApplication?.id;
  const inheritableDbParameters = dbId
    ? documentPartMetadata.parametersMetadata?.find((p) => p.dbId === dbId)?.dimensions
    : undefined;

  switch (coObject?.info?.objectType) {
    case "AMOUNT":
    case "AMOUNT_AIH": {
      const dimensions = (coObject.source as TgkAmountObjectSource)?.dimensions;
      const parameters = coObject?.parameters as DimensionDetail[];
      const updatedAdvanced = coObject?.advanced;

      if (dimensions) {
        coObject.source = {
          ...coObject.source,
          dimensions: getUpdatedDimensions(dimensions, inheritableDbParameters),
        } as TgkAmountObjectSource;
      }

      if (parameters) {
        coObject.parameters = getUpdatedParameters(parameters, inheritableDbParameters);
      }

      if (updatedAdvanced) {
        coObject.advanced = applyInheritedFormats(updatedAdvanced, documentPartMetadata);
      }

      break;
    }
    case "DIMENSION_ATTRIBUTE": {
      const dimensions = (coObject.source as TgkDimensionAttributeSource)?.dimensions;
      if (dimensions) {
        coObject.source = {
          ...coObject.source,
          dimensions: getUpdatedDimensions(dimensions, inheritableDbParameters),
        } as TgkDimensionAttributeSource;
      }
      coObject.source = coObject.source as TgkDimensionAttributeSource;
      break;
    }
    case "TABLE":
    case "CHART": {
      const parameters = coObject.parameters as DimensionDetail[];
      const updatedSource = coObject?.source as TgkTableObjectSource;
      const updatedAdvanced = coObject?.advanced;

      if (parameters) {
        coObject.parameters = getUpdatedParameters(parameters, inheritableDbParameters);
      }

      if (updatedSource) {
        coObject.source = applyInheritedFormatImage(updatedSource, documentPartMetadata);
      }

      if (updatedAdvanced) {
        coObject.advanced = applyInheritedFormats(updatedAdvanced, documentPartMetadata);
      }

      break;
    }
    case "TGK_TEXT": {
      const dimensions = (coObject.source as TgkTextSource)?.dimensions;
      if (dimensions) {
        coObject.source = {
          ...coObject.source,
          dimensions: getUpdatedDimensions(dimensions, inheritableDbParameters),
        } as TgkTextSource;
      }
      break;
    }
    default:
      console.log(`Object with type ${coObject?.info?.objectType} cannot inherit parameters or dimensions`);
  }
};

const applyInheritedFormats = (
  advanced: CoObjectAdvanced,
  documentPartMetadata: DocumentPartMetadata
): CoObjectAdvanced => {
  console.log("Apply inherited formats");
  return advanced?.inheritFormat
    ? {
        ...advanced,
        formatMetadata: documentPartMetadata.formatMetadata,
        formatImage: documentPartMetadata.formatImage,
      }
    : {
        ...advanced,
        formatMetadata: {
          ...advanced?.formatMetadata,
          locale: documentPartMetadata?.formatMetadata?.locale,
        },
      };
};

const applyInheritedFormatImage = (source: TgkTableObjectSource, documentPartMetadata: DocumentPartMetadata) => {
  console.log("Apply inherited image format");
  return source?.inheritFormatImage
    ? {
        ...source,
        formatImage: documentPartMetadata.formatImage,
      }
    : source;
};

export const clearInheritedInfo = (coObject: CoObjectItem) => {
  if (isLegacyComponentInstance(coObject)) return coObject;
  console.log("Removing inherited info from coObject");
  switch (coObject?.info?.objectType) {
    case "AMOUNT":
    case "AMOUNT_AIH": {
      const { originalPayload, ...filteredCoObject } = coObject;
      const { overrideData, overrideStyle, ...filteredInfo } = filteredCoObject.info;
      const source = cleanAmountSource(filteredCoObject.source as TgkAmountObjectSource);
      const updatedParameters = clearInheritedParameters(filteredCoObject.parameters as DimensionDetail[]);
      const updatedDimensions = clearInheritedDimensions(source?.dimensions);
      const updatedAdvanced = clearInheritedFormats(filteredCoObject.advanced);

      if (!updatedDimensions || updatedDimensions.length === 0) {
        delete source.dimensions;
      } else {
        source.dimensions = updatedDimensions;
      }

      if (!updatedParameters || updatedParameters.length === 0) {
        delete filteredCoObject.parameters;
      } else {
        filteredCoObject.parameters = updatedParameters;
      }

      return {
        ...filteredCoObject,
        info: filteredInfo,
        source: {
          ...source,
        },
        advanced: updatedAdvanced,
      };
    }
    case "DIMENSION_ATTRIBUTE": {
      const { originalPayload, advanced, ...filteredCoObject } = coObject;
      const { overrideData, overrideStyle, ...filteredInfo } = filteredCoObject.info;
      const source = filteredCoObject.source as TgkDimensionAttributeSource;
      const updatedDimensions = clearInheritedDimensions(source?.dimensions);

      return {
        ...filteredCoObject,
        info: filteredInfo,
        source: {
          ...source,
          dimensions: updatedDimensions,
        },
      };
    }
    case "TABLE":
    case "CHART": {
      const updatedParameters = clearInheritedParameters(coObject.parameters as DimensionDetail[]);
      const updatedAdvanced = clearInheritedFormats(coObject.advanced);
      const updatedSource = clearInheritedFormatImage(coObject.source as TgkTableObjectSource);
      return {
        ...coObject,
        source: updatedSource,
        parameters: updatedParameters,
        advanced: updatedAdvanced,
      };
    }
    case "TGK_TEXT": {
      const { originalPayload, parameters, advanced, ...filteredCoObject } = coObject;
      const { overrideData, overrideStyle, ...filteredInfo } = filteredCoObject.info;
      const source = filteredCoObject.source as TgkTextSource;
      const updatedDimensions = clearInheritedDimensions(source?.dimensions);

      return {
        ...filteredCoObject,
        info: filteredInfo,
        source: {
          ...source,
          dimensions: updatedDimensions,
          richText: {
            ...source.richText,
            ...(source?.richText?.fieldType ? { fieldType: source.richText.fieldType } : {}),
            ...(source?.richText?.textFormatType ? { textFormatType: source.richText.textFormatType } : {}),
          },
        },
      };
    }
    default:
      console.log(`Object with type ${coObject?.info?.objectType} cannot inherit parameters or dimensions`);
      return coObject;
  }
};

const cleanAmountSource = (source: TgkAmountObjectSource): TgkAmountObjectSource => {
  const cleaned: TgkAmountObjectSource = { ...source };

  if (isDbApplicationEmpty(cleaned.dbApplication)) {
    delete cleaned.dbApplication;
  }

  if (!cleaned.area || cleaned.area.name === null || cleaned.area.name === "") {
    delete cleaned.area;
  }

  if (!cleaned.cell || cleaned.cell === "") {
    delete cleaned.cell;
  }

  if (isEmptyPlainObject(cleaned.analyticalWorkspace)) {
    delete cleaned.analyticalWorkspace;
  }

  if (isEmptyPlainObject(cleaned.dataset)) {
    delete cleaned.dataset;
  }

  return cleaned;
};

const isDbApplicationEmpty = (app: DBApplication): boolean => {
  if (!app) return true;

  return (!app.uid || app.uid === "") && (!app.id || app.id === "") && (!app.desc || app.desc === "");
};

const isEmptyPlainObject = (obj: unknown): obj is Record<string, never> => {
  return obj != null && Object.getPrototypeOf(obj) === Object.prototype && Object.keys(obj).length === 0;
};

const clearInheritedDimensions = (dimensions: DimensionDetail[] = []) => {
  if (!dimensions || dimensions.length === 0) {
    return;
  }

  return dimensions.map((dimension) =>
    dimension.inherited
      ? removeNullOrEmptyFieldsFromDimensionDetail({
          inherited: dimension.inherited,
          reference: dimension.reference,
          dimensionOid: dimension.dimensionOid,
          attributeCode: dimension.attributeCode,
          label: dimension.label,
          type: dimension.type,
        })
      : removeNullOrEmptyFieldsFromDimensionDetail(dimension)
  );
};

const clearInheritedParameters = (parameters: DimensionDetail[] = []) => {
  if (!parameters || parameters.length === 0) {
    return;
  }

  return parameters.map((parameter) =>
    parameter.inherited
      ? {
          inherited: parameter.inherited,
          reference: parameter.reference,
          dimensionOid: parameter.dimensionOid,
          structureCode: parameter.structureCode,
          code: parameter.code,
        }
      : removeNullOrEmptyFieldsFromDimensionDetail(parameter)
  );
};

const removeNullOrEmptyFieldsFromDimensionDetail = (dimensionDetail: DimensionDetail) => {
  const cleaned: DimensionDetail = { ...dimensionDetail };

  if (cleaned.code == null || cleaned.code.trim() === "") delete cleaned.code;
  if (cleaned.structureCode == null || cleaned.structureCode.trim() === "") delete cleaned.structureCode;
  if (cleaned.attributeCode == null || cleaned.attributeCode.trim() === "") delete cleaned.attributeCode;
  if (cleaned.label == null || cleaned.label.trim() === "") delete cleaned.label;
  if (cleaned.type == null || cleaned.type.trim() === "") delete cleaned.type;

  // Remove values if null, empty, or only containing empty objects
  if (Array.isArray(cleaned.values)) {
    const filteredValues = cleaned.values
      .map((v) => {
        if (!v) return null;
        const obj = { ...v };
        Object.keys(obj).forEach((key) => {
          const val = obj[key];
          if (
            val === null ||
            val === undefined ||
            (typeof val === "string" && val.trim() === "") ||
            (Array.isArray(val) && val.length === 0)
          ) {
            delete obj[key];
          }
        });
        return Object.keys(obj).length > 0 ? obj : null;
      })
      .filter((v) => v !== null);

    if (filteredValues.length > 0) {
      cleaned.values = filteredValues;
    } else {
      delete cleaned.values;
    }
  }

  // Remove hierarchy if null or empty object
  if (!cleaned.hierarchy || Object.keys(cleaned.hierarchy).length === 0) {
    delete cleaned.hierarchy;
  } else {
    // Keep only oid, desc, and code from hierarchy
    const allowedKeys = ["oid", "desc", "code"];
    const filteredHierarchy: DimensionHierarchy = {} as DimensionHierarchy;
    allowedKeys.forEach((key) => {
      const value = cleaned.hierarchy?.[key];
      if (
        value !== null &&
        value !== undefined &&
        !(typeof value === "string" && value.trim() === "") &&
        !(Array.isArray(value) && value.length === 0)
      ) {
        filteredHierarchy[key] = value as never;
      }
    });

    if (Object.keys(filteredHierarchy).length > 0) {
      cleaned.hierarchy = filteredHierarchy;
    } else {
      delete cleaned.hierarchy;
    }
  }

  return cleaned;
};

const clearInheritedFormats = (advanced: CoObjectAdvanced) => {
  if (!advanced) {
    return;
  }

  console.log("Clear inherited formats");
  if (advanced.inheritFormat) {
    return {
      choice: advanced.choice,
      inheritFormat: advanced.inheritFormat,
    };
  } else {
    const { documentId, templateInfo, logicId, language, ...others } = advanced;
    return others;
  }
};

const clearInheritedFormatImage = (source: TgkTableObjectSource) => {
  if (!source) {
    return;
  }

  console.log("Clear inherited image format");
  if (source.inheritFormatImage) {
    const { formatImage, ...others } = source;
    return others;
  } else {
    return source;
  }
};

export const allInheritedHasValue = (coObject: CoObjectItem) => {
  const validate = (dimensionDetail: DimensionDetail) => {
    return !dimensionDetail.inherited || dimensionDetail.values?.length > 0;
  };

  return validateDimensionsAndParams(coObject, validate);
};

export const getUpdatedDimensionParameters = (
  dimensionParameters: DimensionParameter[] = [],
  inheritableDimensionParameters: DimensionParameter[] = []
): DimensionParameter[] =>
  dimensionParameters.map((dp) => {
    const selectedDbInheritableDimensionParameter = inheritableDimensionParameters?.find((ip) => ip.dbId === dp.dbId);
    return {
      dbId: dp.dbId,
      dimensions: getUpdatedParameters(dp.dimensions, selectedDbInheritableDimensionParameter?.dimensions ?? []),
    };
  });

const getUpdatedParameters = (parameters: DimensionDetail[], inheritableParameters: DimensionDetail[]) => {
  const newParameters: DimensionDetail[] = [];

  parameters.forEach((p) => {
    if (!p.inherited) {
      newParameters.push(p);
      return;
    }

    const inheritedParameters =
      inheritableParameters
        ?.filter((ip) => ip.structureCode === p.structureCode)
        .map((ip) => ({
          ...ip,
          code: p.code,
          type: p.type,
          label: p.label,
          reference: p.reference,
          inherited: true,
        })) ?? [];

    if (inheritedParameters.length === 0) {
      newParameters.push(...clearInheritedParameters([p]));
    } else {
      newParameters.push(...inheritedParameters);
    }
  });
  return newParameters;
};

const getUpdatedDimensions = (dimensions: DimensionDetail[], inheritableParameters: DimensionDetail[]) => {
  const newDimensions: DimensionDetail[] = [];
  const inheritedMap = new Map<string, DimensionDetail>();
  let attributeDetail: { dimensionOid: string; attributeCode: string } | null = null;

  dimensions.forEach((d) => {
    if (d.attributeCode) {
      attributeDetail = {
        dimensionOid: d.dimensionOid,
        attributeCode: d.attributeCode,
      };
    }

    if (d.inherited) {
      inheritedMap.set(d.dimensionOid, d);
    } else {
      newDimensions.push(d);
    }
  });

  inheritedMap.forEach((dim, oid) => {
    const enriched = getEnrichedInheritedParameter(oid, dim, attributeDetail, inheritableParameters);

    if (enriched.length === 0) {
      newDimensions.push(...clearInheritedDimensions([dim]));
    } else {
      const updatedDimensions = enriched.map((id) => ({
        ...id,
        code: dim.code,
        type: dim.type,
        label: dim.label,
        inherited: true,
      }));
      newDimensions.push(...updatedDimensions);
    }
  });

  return newDimensions;
};

const getEnrichedInheritedParameter = (
  oid: string,
  dimension: DimensionDetail,
  attributeDetail: { dimensionOid: string; attributeCode: string } | null,
  inheritableParameters: DimensionDetail[]
): DimensionDetail[] => {
  const isFirstAttr = attributeDetail?.dimensionOid == oid;

  return (
    inheritableParameters
      ?.filter((p) => p.dimensionOid === oid)
      .map((p) => ({
        ...p,
        inherited: true,
        label: dimension.label,
        type: dimension.type,
        reference: dimension.reference,
        ...(isFirstAttr ? { attributeCode: attributeDetail.attributeCode } : {}),
      })) ?? []
  );
};

export const checkMissingInheritableParameters = (
  parameters: Array<DimensionParameter>,
  inheritableParameters: Array<DimensionParameter>
): Array<DimensionParameter> => {
  // check if all the inheritable params have a corresponding inheritable parameter
  const missing: Array<DimensionParameter> = [];
  parameters.forEach((param) => {
    if (!param.dbId || !param.dimensions) return;
    const inheritedParams = param.dimensions.filter((d) => d.inherited);
    if (inheritedParams.length === 0) return;

    const inheritableParams = inheritableParameters?.find((ip) => ip.dbId === param.dbId);
    inheritedParams.forEach((inherited) => {
      const found = inheritableParams?.dimensions?.some(
        (inheritable) => inheritable.dimensionOid === inherited.dimensionOid
      );

      if (!found) {
        // Try to find if we already have an entry for this dbId
        let entry = missing.find((m) => m.dbId === param.dbId);
        if (!entry) {
          entry = { dbId: param.dbId, dimensions: [] };
          missing.push(entry);
        }
        entry.dimensions.push(inherited);
      }
    });
  });

  return missing;
};

const SINGLE_VALUE_INHERIT_OBJECT_TYPES: CoObjectTypeEnum[] = [
  CoObjectTypeEnum.AMOUNT,
  CoObjectTypeEnum.AMOUNT_AIH,
  CoObjectTypeEnum.DIMENSION_ATTRIBUTE,
  CoObjectTypeEnum.TGK_TEXT,
];

const inheritsMoreThanOneValue = (
  items: DimensionDetail[],
  inheritableParameters: DimensionDetail[],
  matchBy: (item: DimensionDetail, inheritable: DimensionDetail) => boolean
): boolean =>
  (items ?? [])
    .filter((item) => item?.inherited)
    .some((item) => {
      const inheritable = inheritableParameters?.find((p) => matchBy(item, p));
      return (inheritable?.values?.length ?? 0) > 1;
    });

export const hasInheritMoreThanOneValueViolation = (
  coObject: CoObjectItem,
  inheritableParameters: DimensionDetail[],
  formParametersMetadata: TgkParameterMetadata[] = []
): boolean => {
  if (!SINGLE_VALUE_INHERIT_OBJECT_TYPES.includes(coObject?.info?.objectType as CoObjectTypeEnum)) {
    return false;
  }
  const source = coObject?.source as TgkAmountObjectSource;
  const isFinancialAmount =
    coObject.info.objectType === CoObjectTypeEnum.AMOUNT &&
    source?.type !== "AMOUNT_AIH" &&
    (!source?.objectSource || source.objectSource === SourceTypeEnum.TAGETIK);
  const singleValueInheritableParameters = isFinancialAmount
    ? inheritableParameters?.filter((p) => p.structureCode === "PER")
    : inheritableParameters;
  const dimensions = source?.dimensions ?? [];
  const parameters = (coObject?.parameters as DimensionDetail[]) ?? [];
  const singleValueParameters =
    coObject.info.objectType === CoObjectTypeEnum.AMOUNT &&
    source?.type !== "AMOUNT_AIH" &&
    source?.objectSource === SourceTypeEnum.TAGETIK_FORM
      ? parameters.filter(
          (parameter) =>
            !formParametersMetadata.some(
              (metadata) =>
                metadata.type === "dimension" &&
                metadata.dimensionCode === parameter.structureCode &&
                metadata.code === parameter.code &&
                metadata.multiSelectable === true
            )
        )
      : parameters;
  return (
    inheritsMoreThanOneValue(
      dimensions,
      singleValueInheritableParameters,
      (item, p) => p.dimensionOid === item.dimensionOid
    ) ||
    inheritsMoreThanOneValue(
      singleValueParameters,
      singleValueInheritableParameters,
      (item, p) => p.structureCode === item.structureCode
    )
  );
};

/**
 * Notifies the user (and returns true) when the object inherits more than one value for a
 * dimension it can only hold once. Returns false when there is no violation.
 */
export const notifyInheritMoreThanOneValue = (
  coObject: CoObjectItem,
  inheritableParameters: DimensionDetail[],
  formParametersMetadata: TgkParameterMetadata[] = []
): boolean => {
  const hasViolation = hasInheritMoreThanOneValueViolation(coObject, inheritableParameters, formParametersMetadata);
  if (hasViolation) {
    NotificationManager.addNotification({
      key: "inherit.morethanonevalue.error",
      title: "",
      message: formatMessage("cof.createnewobject.inherit.morethanonevalue.error"),
      type: "error",
      isTemporary: false,
    });
  }
  return hasViolation;
};
